import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { SortOptions, DateRangeType, ShipmentDateFilterType } from '@shared-features/enums/shared-enums.enum';
import { TabModel } from '../../models/tab.model';
import { FormControl } from '@angular/forms';
import { DateRange } from '@shared/models/date-range';
import { BaseComponent } from '@shared-features/components/base-component/base.component';
import { ColumnOptionsService } from '../../services/column-options.service';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { ColumnOptionsListPage } from '@shared-features/enums/layout.enum';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { ShipmentRequestsMultiplicationType, TrackingType } from '@shared-features/enums/shipments.enum';
import { EnumToArrayPipe } from '../../pipes/enum-to-array.pipe';
import debounce from 'lodash/debounce';

@Component({
  selector: 'app-list-filter-controls',
  templateUrl: './list-filter-controls.component.html',
  styleUrls: ['./list-filter-controls.component.scss'],
})
export class ListFilterControlsComponent extends BaseComponent implements OnInit, OnChanges {
  @Input() includeSearchControl = true;
  @Input() includeDateControl = false;
  @Input() includeSortByControl = true;
  @Input() includeFilterButton = true;
  @Input() includeBayanTypeFilter = false;

  @Input() filterNavigationConfig: TabModel;
  @Input() searchPlaceholder: string;
  @Input() searchTerm: string;
  @Input() showCustomDateOption = false;
  @Input() selectedDateRange: DateRangeType;
  @Input() customStartFrom: string;
  @Input() customEndAt: string;
  @Input() isBayanVerified: boolean;

  @Output() sortBy = new EventEmitter();
  @Output() searchValue = new EventEmitter();
  @Output() selectedDateRangeChanged = new EventEmitter();
  @Input() showSearchActions = true;
  @Input() multipleSearch = true;
  @Input() searchIconPosition: 'left' | 'right' = 'left';
  @Input() includePickupDateControl = false;
  @Input() ShipmentDateFilterType: any = ShipmentDateFilterType;
  @Input() shipmentRequestFilterCount: ShipmentRequestsMultiplicationType;
  @Input() trackingType: TrackingType;
  @Input() includeExportMenuButton = false;
  @Input() includePickupDateLabel = false;
  @Output() startFrom = new EventEmitter();
  @Output() endAt = new EventEmitter();
  @Output() shipmentRequestsMultiplicationTypeFilter = new EventEmitter();
  @Output() trackingTypeFilter = new EventEmitter();
  @Output() selectedShipmentDateFilterTypeChanged = new EventEmitter();
  @Output() bayanTypeFilter = new EventEmitter();
  @Input() columnOptions;
  @Output() columnOptionsChanges = new EventEmitter();
  @Input() includeColumnOptions = false;
  @Output() exportedColumnsFilter = new EventEmitter();
  @Output() exportCsv = new EventEmitter<void>();
  @Output() exportPDF = new EventEmitter<void>();
  @Output() handleExport = new EventEmitter<void>();
  shipmentListExportFilter = [];
  trackingTypeControl = new FormControl(null);
  bayanTypeFilterControl = new FormControl(null);
  TrackingType = TrackingType;
  // enum
  public sortOptions = SortOptions;
  shipmentRequestsMultiplicationTypeControl = new FormControl(null);
  ShipmentRequestsMultiplicationType = ShipmentRequestsMultiplicationType;

  dateRangeControl = new FormControl<DateRange>(
    Object.assign(new DateRange(), { range: DateRangeType.ALL_TIME, startFrom: '', endAt: '' })
  );
  @Input() shipmentDateFilterTypeValue: ShipmentDateFilterType = this.getDateTypeFilterValue();
  @Input() maxDate: Date = new Date();

  // at most cases it should be enum of key in entity = key in translation
  @Input() columns: { [key: string]: string };
  // it must list of keys in entity provided in columnOptionKeys
  @Input() defaultVisibleColumns: string[];
  @Input() pageName: ColumnOptionsListPage;

  constructor(
    private columnOptionsService: ColumnOptionsService,
    private changeDetector: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.columnOptionsService.state$.pipe(debounceTime(250), takeUntil(this.destroy$)).subscribe(({ state, keys }) => {
      this.shipmentListExportFilter = (state || []).map(({ key }) => key);
      this.exportedColumnsFilter.emit(this.shipmentListExportFilter);
      this.changeDetector.markForCheck();
      this.columnOptionsChanges.emit();
    });

    this.dateRangeControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((dateRange) => this.onDateRangeChange(dateRange));
  }

  ngOnChanges(changes: SimpleChanges): void {
    const { selectedDateRange, shipmentRequestFilterCount, trackingType, isBayanVerified,
      customStartFrom, customEndAt
    } = changes;
    if (
      (selectedDateRange && selectedDateRange.currentValue !== selectedDateRange.previousValue) ||
      (customStartFrom && customStartFrom.currentValue !== customStartFrom.previousValue) ||
      (customEndAt && customEndAt.currentValue !== customEndAt.previousValue)
    ) {
      this.syncDateRangeControl();
    }
    if (shipmentRequestFilterCount && shipmentRequestFilterCount.currentValue !== shipmentRequestFilterCount.previousValue) {
      this.shipmentRequestsMultiplicationTypeControl.setValue(this.shipmentRequestFilterCount);
    }
    if (trackingType && trackingType.currentValue !== trackingType.previousValue) {
      this.trackingTypeControl.setValue(this.trackingType);
    }
    if (isBayanVerified && isBayanVerified.currentValue !== isBayanVerified.previousValue) {
      this.bayanTypeFilterControl.setValue(this.isBayanVerified);
    }
    debounce(() => this.changeDetector.markForCheck(), 500);
  }

  private syncDateRangeControl(): void {
    const range = this.selectedDateRange ?? DateRangeType.ALL_TIME;
    const isCustom = range === DateRangeType.CUSTOM;
    const current = this.dateRangeControl.value;
    this.dateRangeControl.setValue(
      Object.assign(new DateRange(), {
        range,
        startFrom: isCustom ? this.customStartFrom || current?.startFrom || '' : '',
        endAt: isCustom ? this.customEndAt || current?.endAt || '' : '',
      }),
      { emitEvent: false }
    );
  }

  onSearch(value: any) {
    this.searchValue.emit(value);
    this.selectedDateRangeChanged.emit(DateRangeType.ALL_TIME);
  }

  onSelectSortBy(event: any) {
    this.sortBy.emit(event);
  }

  onDateRangeChange(dateRange: DateRange): void {
    if (!dateRange) {
      return;
    }
    const { range, startFrom, endAt } = dateRange;
    if (range === DateRangeType.CUSTOM) {
      this.startFrom.emit(startFrom);
      this.endAt.emit(endAt);
    }
    this.selectedDateRangeChanged.emit(range);
  }

  onShipmentRequestsMultiplicationTypeChanges(event: MatButtonToggleChange) {
    this.shipmentRequestsMultiplicationTypeFilter.emit(event.value);
  }

  ontrackingTypeChanges(event: MatButtonToggleChange): void {
    this.trackingTypeFilter.emit(event.value);
  }

  ontBayanTypeChanges(event: MatButtonToggleChange): void {
    this.bayanTypeFilter.emit(event.value);
  }

  toggleColumnOptionsMenu(): void {
    this.columnOptionsService.toggleDrawer(true);
  }

  private getDateTypeFilterValue(): ShipmentDateFilterType {
    const types = new EnumToArrayPipe().transform(this.ShipmentDateFilterType);
    return types[1] ? ShipmentDateFilterType.pickUpDate : ShipmentDateFilterType.createdAt;
  }

  handleExportCsv(): void {
    this.exportCsv.emit();
  }

  onExportPDF(): void {
    this.exportPDF.emit();
  }

  onHandleExport(): void {
    this.handleExport.emit();
  }
}
